/**
 * LoRa Device Setup Panel
 *
 * Panel for connecting to and configuring LoRa mesh devices.
 * Supports both Meshtastic and MeshCore backends.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import { useState } from 'react'
import { clsx } from 'clsx'
import { useLoRa } from '@/hooks/useLoRa'
import type { LoRaBackend } from '@/lib/lora'
import { LoRaBackendBadge } from './LoRaStatus'
import { RadioNetworkPanel } from './RadioNetworkPanel'

interface DeviceSetupPanelProps {
  onClose?: () => void
  /** The open room, so its radio channel can be shared with the MeshCore app. */
  roomId?: string
  roomSecret?: string
}

export function DeviceSetupPanel({ onClose, roomId, roomSecret }: DeviceSetupPanelProps) {
  const {
    isAvailable,
    availableBackends,
    status,
    device,
    isConnecting,
    isConnected,
    error,
    connect,
    disconnect,
    getBackendName,
  } = useLoRa()

  const [selectedBackend, setSelectedBackend] = useState<LoRaBackend | undefined>(undefined)

  const handleConnect = async () => {
    await connect(selectedBackend)
  }

  const handleDisconnect = () => {
    disconnect()
  }

  if (!isAvailable) {
    return (
      <div className="p-4 rounded-xl bg-card-dark border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">📻</span>
          <div>
            <h3 className="font-display font-bold text-white">LoRa Mesh</h3>
            <p className="text-xs text-white/50">Web Bluetooth not available</p>
          </div>
        </div>

        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-sm text-red-400">
            Web Bluetooth is required for LoRa device connection.
          </p>
          <ul className="mt-2 text-xs text-white/50 space-y-1">
            <li>• Use Chrome, Edge, or Opera on Android</li>
            <li>• Use Chrome or Edge on Windows/macOS</li>
            <li>• Safari and Firefox are not supported</li>
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 rounded-xl bg-card-dark border border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📻</span>
          <div>
            <h3 className="font-display font-bold text-white flex items-center gap-2">
              LoRa Mesh
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-yellow-500/20 text-yellow-400">
                Experimental
              </span>
            </h3>
            <p className="text-xs text-white/50">
              {isConnected ? 'Connected' : 'Connect to a LoRa device'}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-sm text-red-400">{error.message}</p>
        </div>
      )}

      {/* Connected device info */}
      {isConnected && device && (
        <div className="mb-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-purple-400">{device.name}</span>
            <LoRaBackendBadge backend={device.backend} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-white/50">
            <div>
              <span className="text-white/30">Firmware:</span>{' '}
              <span className="text-white/70">{device.firmwareVersion}</span>
            </div>
            {device.backend === 'meshtastic' && (
              <>
                <div>
                  <span className="text-white/30">Battery:</span>{' '}
                  <span className="text-white/70">{device.batteryLevel}%</span>
                </div>
                <div>
                  <span className="text-white/30">GPS:</span>{' '}
                  <span className="text-white/70">{device.hasGps ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="text-white/30">Region:</span>{' '}
                  <span className="text-white/70">{device.region}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Which mesh the radio is on (MeshCore) */}
      {isConnected && device?.backend === 'meshcore' && (
        <div className="mb-4">
          <RadioNetworkPanel roomId={roomId} roomSecret={roomSecret} />
        </div>
      )}

      {/* Backend selection (when not connected) */}
      {!isConnected && availableBackends.length > 0 && (
        <div className="mb-4">
          <label className="text-xs text-white/50 mb-2 block">Select Protocol</label>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedBackend(undefined)}
              className={clsx(
                'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                'border',
                selectedBackend === undefined
                  ? 'bg-primary/20 border-primary text-primary'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
              )}
            >
              Auto
            </button>
            {availableBackends.map((backend) => (
              <button
                key={backend}
                onClick={() => setSelectedBackend(backend)}
                className={clsx(
                  'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  'border',
                  selectedBackend === backend
                    ? 'bg-primary/20 border-primary text-primary'
                    : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                )}
              >
                {getBackendName(backend)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Connection button */}
      {!isConnected ? (
        <button
          onClick={handleConnect}
          disabled={isConnecting}
          className={clsx(
            'w-full px-4 py-3 rounded-xl font-bold text-sm uppercase tracking-wider',
            'transition-all duration-200',
            isConnecting
              ? 'bg-yellow-500/20 text-yellow-400 cursor-wait'
              : 'bg-purple-500 text-white hover:bg-purple-400 shadow-hard-sm hover:shadow-hard'
          )}
        >
          {isConnecting ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
              Scanning...
            </span>
          ) : (
            'Connect to LoRa Device'
          )}
        </button>
      ) : (
        <button
          onClick={handleDisconnect}
          className={clsx(
            'w-full px-4 py-3 rounded-xl font-bold text-sm uppercase tracking-wider',
            'bg-white/10 text-white/70 hover:bg-red-500/20 hover:text-red-400',
            'transition-all duration-200 border border-white/10'
          )}
        >
          Disconnect
        </button>
      )}

      {/* Status indicator */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/40">
        <span
          className={clsx(
            'w-2 h-2 rounded-full',
            isConnected
              ? 'bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.8)]'
              : isConnecting
              ? 'bg-yellow-500 animate-pulse'
              : 'bg-gray-500'
          )}
        />
        <span>
          {status === 'ready'
            ? 'Ready'
            : status === 'connected'
            ? 'Connected'
            : status === 'configuring'
            ? 'Configuring...'
            : status === 'connecting'
            ? 'Connecting...'
            : status === 'scanning'
            ? 'Scanning...'
            : 'Disconnected'}
        </span>
      </div>

      {/* Info text */}
      <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <p className="text-[10px] text-blue-400/80 leading-relaxed">
          <strong>Supported devices:</strong> Meshtastic (T-Beam, Heltec V3, RAK4631, etc.)
          and MeshCore compatible devices. Requires Bluetooth enabled on your phone and
          a compatible browser (Chrome, Edge).
        </p>
      </div>
    </div>
  )
}
